/**
 * RadarValuesGL.tsx — غلاف React لطبقة الرادار على طريقة Windy (RadarFlowGLLayer).
 *
 * كل شيء أمريّ: الطبقة تُضاف مرّة، والزمن يصلها من ساعة التشغيل مباشرةً لا عبر React
 * (راجع التعليل في RadarDomainTiles: ستّون تصييراً في الثانية كانت أكبر حاجزٍ للخيط).
 * والإطارات وحقل الحركة يتبدّلان كل بضع دقائق فقط، فيمرّان بالخصائص.
 */

import { memo, useEffect, useRef } from 'react';
import { useMapRef } from './MapContext';
import { RadarFlowGLLayer, type RadarFlowField, type RadarGLFrame } from './webgl/RadarFlowGLLayer';
import { radarRastersBase, type RadarDomain } from '../../services/mrmsRadarService';
import { radarRgba } from '../../config/radarPalette';
import { subscribePlayback, type PlaybackPosition } from '../../services/playbackClock';
import { radarDebugContext, radarDebugOn, radarDebugTick } from '../../services/radarDebug';

const LAYER_ID = 'radar-flow-gl';
/** نفس عتامة مسار الألوان (‏RADAR_ALPHA في RadarDomainTiles): الطرق تُرى من تحت الصدى. */
const RADAR_ALPHA = 0.8;
/** عتبة الصدى — تطابق `MIN_DBZ` في mrms_to_raster.py (ما دونها لا يُلوَّن). */
const MIN_DBZ = 5;

/** جدول الألوان: خانة لكل بايت قيمة، من نفس محطّات المفتاح والمولّد (radarPalette.json). */
function buildLut(enc: { base: number; step: number }): Uint8Array {
    const lut = new Uint8Array(256 * 4);
    for (let b = 0; b < 256; b++) {
        const dbz = enc.base + b * enc.step;
        if (dbz < MIN_DBZ) continue;              // شفّاف — كما يفعل `colorize` في المولّد
        const [r, g, bl, a] = radarRgba(dbz);
        lut.set([r, g, bl, Math.round(Math.max(0, Math.min(1, a)) * 255)], b * 4);
    }
    return lut;
}

function epochOf(time: string | null | undefined): number | null {
    const ms = time ? Date.parse(time) : NaN;
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

/*
 * ── حارس التقدّم للتشغيل التلقائي ────────────────────────────────────────────
 * الحلقة لا تعبر إلى إطارٍ لم تصل بلاطاته للشاشة؛ تتباطأ تحته حتى يصل (useAutoplay).
 * كان محور الرادار بلا حارس لأن الخانات كانت تستعير ما يُعرض — والاستعارة موجودة هنا
 * أيضاً، لكنّ عرض إطارٍ مستعار هو بالضبط «الرجوع للخلف» الذي نعالجه.
 */
let readyAt: ((axisIndex: number) => boolean) | null = null;
export function radarGLCanAdvance(axisIndex: number): boolean {
    return readyAt ? readyAt(axisIndex) : true;
}

export const RadarValuesGL = memo(function RadarValuesGL({ domain, times, flow, beforeId }: {
    domain: RadarDomain; times: readonly number[]; flow: RadarFlowField | null; beforeId: string | undefined;
}) {
    const mapRef = useMapRef();
    const layerRef = useRef<RadarFlowGLLayer | null>(null);
    const enc = domain.values;
    const beforeIdRef = useRef(beforeId);
    beforeIdRef.current = beforeId;

    // ── الطبقة نفسها ───────────────────────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map || !enc) return;
        const layer = new RadarFlowGLLayer(LAYER_ID, radarRastersBase(), buildLut(enc), RADAR_ALPHA);
        layerRef.current = layer;
        // للقياس: ‏`__radarGL.stats` و`__radarGL.loadedShare()` من وحدة التحكّم.
        if (radarDebugOn) (window as unknown as { __radarGL?: RadarFlowGLLayer }).__radarGL = layer;
        let cancelled = false;
        const add = (): boolean => {
            if (cancelled) return true;
            // «محلَّل» لا «محمَّل» — راجع بوّابة النمط في RadarDomainTiles.
            const parsed = !!(map as unknown as { style?: { _loaded?: boolean } }).style?._loaded;
            if (!parsed && !map.isStyleLoaded()) return false;
            try {
                if (!map.getLayer(LAYER_ID)) {
                    map.addLayer(layer, beforeIdRef.current && map.getLayer(beforeIdRef.current)
                        ? beforeIdRef.current : undefined);
                }
            } catch { return false; }
            return true;
        };
        let timer = 0;
        if (!add()) timer = window.setInterval(() => { if (add()) window.clearInterval(timer); }, 200);
        // تبديل الخريطة الأساس يُعيد النمط ويُسقط الطبقات المخصّصة — تُعاد حينها.
        const onStyle = () => { if (!map.getLayer(LAYER_ID)) add(); };
        map.on('styledata', onStyle);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
            map.off('styledata', onStyle);
            try { if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID); } catch { /* الخريطة تُفكَّك */ }
            layerRef.current = null;
            readyAt = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapRef, enc?.base, enc?.step]);

    // المرساة قد تصل بعد الإضافة (النمط يجهز متأخّراً) — تُنقل الطبقة حينها تحت الخطوط.
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map || !beforeId) return;
        try { if (map.getLayer(LAYER_ID) && map.getLayer(beforeId)) map.moveLayer(LAYER_ID, beforeId); } catch { /* قيد الإضافة */ }
    }, [mapRef, beforeId]);

    // ── الإطارات وحقل الحركة ───────────────────────────────────────────────
    useEffect(() => {
        const frames: RadarGLFrame[] = [];
        for (const f of domain.frames) {
            const t = epochOf(f.time);
            if (t !== null && f.tiles) frames.push({ t, prefix: f.tiles });
        }
        layerRef.current?.setFrames(frames);
    }, [domain]);

    useEffect(() => { layerRef.current?.setFlow(flow); }, [flow, domain]);

    // ── الزمن من ساعة التشغيل ──────────────────────────────────────────────
    useEffect(() => {
        const epochAt = (p: PlaybackPosition): number | null => {
            if (!times.length) return null;
            const i = Math.max(0, Math.min(times.length - 1, p.index));
            const a = times[i];
            const b = times[Math.min(i + 1, times.length - 1)];
            return a + (b - a) * p.fraction;
        };
        readyAt = (i) => {
            const l = layerRef.current;
            const t = times[Math.max(0, Math.min(times.length - 1, i))];
            return !l || t === undefined || l.isReadyAt(t);
        };
        let last = 0;
        const unsub = subscribePlayback((p) => {
            const e = epochAt(p);
            const l = layerRef.current;
            if (e === null || !l) return;
            l.setEpoch(e);
            if (radarDebugOn) {
                radarDebugTick();
                const now = performance.now();
                if (now - last > 250) {
                    last = now;
                    radarDebugContext({
                        idx: p.index, frac: +p.fraction.toFixed(3), gl: true,
                        nsReady: l.isReadyAt(e), loaded: +l.loadedShare().toFixed(3), ...l.stats,
                    });
                }
            }
        });
        return () => { unsub(); };
    }, [times, domain]);

    return null;
});
